import { initializeApp } from "firebase/app";
import { getMessaging, getToken, onMessage, Messaging, isSupported, deleteToken } from "firebase/messaging";
import { getAnalytics } from "firebase/analytics";
import api from "./api";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

let messaging: Messaging | null = null;
let tokenRegistrationInProgress = false;
let tokenRegistrationSucceeded = false;

const initFirebase = async () => {
  try {
    const supported = await isSupported();
    
    if (supported) {
      const app = initializeApp(firebaseConfig);
      messaging = getMessaging(app);
      
      // Ініціалізуємо аналітику для збору статистики повідомлень
      if (firebaseConfig.measurementId) {
        getAnalytics(app);
        console.log('[Firebase] Analytics initialized');
      }

      console.log('[Firebase] Messaging initialized successfully');
    } else {
      console.warn('[Firebase] Messaging not supported. Attempting to clean corrupted DB...');
      try {
         // Fallback: якщо база пошкоджена, видаляємо її для відновлення роботи
         const req = window.indexedDB.deleteDatabase('firebase-messaging-database');
         req.onsuccess = () => console.log('[Firebase] DB deleted. Please reload the page if issues persist.');
      } catch(e) {
         console.error('[Firebase] Failed to delete DB:', e);
      }
    }
  } catch (error) {
    console.error("[Firebase] Initialization error:", error);
  }
};

export const requestNotificationPermission = async (): Promise<string | null> => {
  console.log('[Firebase] requestNotificationPermission called');

  if (tokenRegistrationSucceeded) {
    console.log('[Firebase] Token already registered this session, skipping');
    return null;
  }
  if (tokenRegistrationInProgress) {
    // Захист від дубля спрацював правильно - не warn (шум у BetterStack).
    console.log('[Firebase] Token registration already in progress, skipping duplicate call');
    return null;
  }
  tokenRegistrationInProgress = true;

  if (!messaging) {
    await initFirebase();
    if (!messaging) {
      console.warn('[Firebase] Messaging not initialized, skipping token registration');
      return null;
    }
  }

  try {
    const permission = await Notification.requestPermission();
    console.log('[Firebase] Notification.permission:', permission);

    if (permission === 'granted') {
      const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
      if (!vapidKey) {
        console.error('[Firebase] VAPID key is missing');
        return null;
      }

      // 1. Оновлення SW
      try {
        const registrations = await navigator.serviceWorker.getRegistrations();
        console.log('[Firebase] Existing SW registrations:', registrations.map(r => r.scope));
        for (const reg of registrations) {
          if (reg.scope.includes(window.location.origin)) {
            await reg.update();
          }
        }
      } catch (e) {
        console.warn('[Firebase] Error checking old SWs:', e);
      }

      // 2. Реєстрація
      console.log('[Firebase] Registering SW...');
      const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
        scope: '/',
      });
      console.log('[Firebase] SW registered, state:', registration.active?.state ?? 'no active worker');

      // Чекаємо activation саме цієї реєстрації (не просто будь-якої через .ready)
      if (!registration.active) {
        const sw = registration.installing ?? registration.waiting;
        if (sw) {
          console.log('[Firebase] SW not yet active, waiting for activation...');
          await new Promise<void>((resolve, reject) => {
            const timeout = setTimeout(() => reject(new Error('SW activation timeout')), 15000);
            sw.addEventListener('statechange', (e) => {
              const state = (e.target as ServiceWorker).state;
              console.log('[Firebase] SW state changed:', state);
              if (state === 'activated') {
                clearTimeout(timeout);
                resolve();
              } else if (state === 'redundant') {
                clearTimeout(timeout);
                reject(new Error('SW became redundant'));
              }
            });
          });
        }
      }
      console.log('[Firebase] SW active, state:', registration.active?.state);
      
      // Діагностика: перевіряємо чи pushManager може зробити subscribe
      try {
        console.log('[Firebase] Testing pushManager.permissionState...');
        const permState = await registration.pushManager.permissionState({ userVisibleOnly: true });
        console.log('[Firebase] pushManager.permissionState:', permState);
      } catch (e) {
        console.warn('[Firebase] pushManager.permissionState failed:', e);
      }

      // Функція отримання токена
      const getTokenSafe = async () => {
        const timeoutPromise = new Promise<null>((_, reject) =>
          setTimeout(() => {
            console.error('[Firebase] getToken timed out after 10 seconds');
            reject(new Error('Timeout getting token'));
          }, 10000)
        );

        console.log('[Firebase] Starting getToken with vapidKey length:', vapidKey.trim().length);
        const tokenPromise = getToken(messaging!, {
          vapidKey: vapidKey.trim(),
          serviceWorkerRegistration: registration
        });

        return Promise.race([tokenPromise, timeoutPromise]);
      };

      // Діагностика pushManager перед getToken
      try {
        const existingSub = await registration.pushManager.getSubscription();
        console.log('[Firebase] Existing push subscription:', existingSub ? existingSub.endpoint.slice(0, 60) + '...' : 'none');
        if (existingSub) {
          const expirationTime = existingSub.expirationTime;
          console.log('[Firebase] Subscription expiration:', expirationTime ? new Date(expirationTime).toISOString() : 'never');
        }
      } catch (e) {
        console.warn('[Firebase] pushManager.getSubscription() failed:', e);
      }

      console.log('[Firebase] Calling getToken...');
      try {
        const token = await getTokenSafe();
        if (token) {
          console.log('[Firebase] Token received:', token);
          await sendTokenToBackend(token);
          tokenRegistrationSucceeded = true;
          return token;
        } else {
          console.warn('[Firebase] getToken returned null/undefined (attempt 1)');
        }
      } catch (error) {
        console.warn('[Firebase] First attempt failed:', error);

        try {
          await deleteToken(messaging!);
          console.log('[Firebase] Old token deleted, retrying...');
        } catch (e) { /* ignore */ }

        try {
          const token = await getTokenSafe();
          if (token) {
            console.log('[Firebase] Token received on retry:', token);
            await sendTokenToBackend(token);
            tokenRegistrationSucceeded = true;
            return token;
          } else {
            console.warn('[Firebase] getToken returned null/undefined (retry)');
          }
        } catch (retryError) {
          console.error('[Firebase] Retry failed:', retryError);
        }
      }
    } else {
      // Користувач сам заборонив/не дозволив пуші - це вибір, а не проблема: не warn (було ~13 записів за добу).
      console.log('[Firebase] Permission not granted, status:', permission);
    }
  } catch (err) {
    console.error('[Firebase] Unexpected error in requestNotificationPermission:', err);
  } finally {
    tokenRegistrationInProgress = false;
  }
  return null;
};

const sendTokenToBackend = async (token: string) => {
  console.log('[Firebase] Sending token to backend, platform: web, token:', token.slice(0, 20) + '...');
  try {
    const res = await api.post('/push/device', { token, platform: 'web' });
    console.log('[Firebase] Token saved to backend successfully:', res.data);
  } catch (error: any) {
    console.error('[Firebase] Failed to save token to backend:', {
      status: error?.response?.status,
      data: error?.response?.data,
      message: error?.message,
    });
  }
};

export const onForegroundMessage = (callback: (payload: any) => void) => {
  if (!messaging) return;
  return onMessage(messaging, (payload) => {
    console.log('[Firebase] Message received in foreground: ', payload);
    callback(payload);
  });
};

// Функція повного скидання налаштувань сповіщень
export const resetNotificationData = async () => {
  console.log('[Firebase] Starting full reset...');
  
  if (!messaging) {
    await initFirebase();
  }

  // 1. Видаляємо токен з Firebase
  if (messaging) {
    try {
      await deleteToken(messaging);
      console.log('[Firebase] Token deleted from messaging');
    } catch (e) {
      console.warn('[Firebase] Error deleting token:', e);
    }
  }

  // 2. Видаляємо Service Workers
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    for (const registration of registrations) {
      await registration.unregister();
      console.log('[Firebase] Service Worker unregistered:', registration.scope);
    }
  } catch (e) {
    console.error('[Firebase] Error unregistering SW:', e);
  }

  // 3. Чистимо IndexedDB (де Firebase зберігає токени)
  try {
    const dbs = await window.indexedDB.databases();
    dbs.forEach(db => {
      if (db.name && db.name.includes('firebase')) {
        window.indexedDB.deleteDatabase(db.name);
        console.log('[Firebase] Database deleted:', db.name);
      }
    });
  } catch (e) {
    // Fallback для старих браузерів
    window.indexedDB.deleteDatabase('firebase-messaging-database');
    console.warn('[Firebase] Error clearing DB (might be normal):', e);
  }

  console.log('[Firebase] Reset complete. Reload required.');
};
